import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { fileToBase64, countXlsxDataRows } from "@/lib/bulk-xlsx";
import type { BulkImportResult } from "@shared/bulk/types";
import {
  Download,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
} from "lucide-react";

/** Per-entity config for one import/export flow (one tab). */
export interface BulkEntityConfig {
  /** Stable tab id (only used in tabbed mode). */
  value: string;
  /** Tab label (only shown in tabbed mode). */
  label: string;
  /** GET endpoint that streams a blank .xlsx template download. When omitted,
   *  no "Download template" button is shown — use exportUrl only instead. */
  templateUrl?: string;
  /** GET endpoint that streams the .xlsx export download. */
  exportUrl: string;
  /** POST endpoint accepting `{ xlsxBase64 }` and returning a BulkImportResult. */
  importUrl: string;
  /** Query key invalidated after a successful import so the list refetches. */
  invalidateQueryKey: readonly unknown[];
  /** Plural noun for the entity, e.g. "properties". */
  entityNounPlural: string;
}

interface BulkImportExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dialog heading, e.g. "Bulk Import / Export". */
  title: string;
  /** Optional sub-heading describing the entity. */
  description?: string;
  /**
   * Multi-entity (tabbed) mode. When two or more entities are supplied, the
   * dialog renders a tab per entity. When omitted, the legacy single-entity
   * props below are used.
   */
  tabs?: BulkEntityConfig[];
  // ── Legacy single-entity props (used when `tabs` is omitted) ──
  templateUrl?: string;
  exportUrl?: string;
  importUrl?: string;
  invalidateQueryKey?: readonly unknown[];
  entityNounPlural?: string;
}

/**
 * One self-contained import/export flow: download a template, export existing
 * rows, pick a file, confirm the row count, upload, then review a per-row
 * outcome report. Holds its own file/result/mutation state so each tab is
 * isolated. Reports its in-flight state up via `onPendingChange` so the parent
 * dialog can gate dismissal (docs/dialog-dismissal-convention.md).
 */
function BulkEntityPanel({
  entity,
  onPendingChange,
}: {
  entity: BulkEntityConfig;
  onPendingChange: (pending: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingRowCount, setPendingRowCount] = useState<number | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const xlsxBase64 = await fileToBase64(file);
      const res = await apiRequest("POST", entity.importUrl, { xlsxBase64 });
      return (await res.json()) as BulkImportResult;
    },
    onSuccess: (data) => {
      setResult(data);
      setPendingFile(null);
      setPendingRowCount(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: entity.invalidateQueryKey });
      toast({
        title: "Import complete",
        description: `${data.created} created, ${data.updated} updated, ${data.skipped} skipped, ${data.errors} errors.`,
      });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Import failed",
        description: parseErrorMessage(err, "Could not import the file. Please check the format and try again."),
      });
    },
  });

  // Surface in-flight state so the parent dialog can block dismissal.
  useEffect(() => {
    onPendingChange(importMutation.isPending);
  }, [importMutation.isPending, onPendingChange]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setResult(null);
    if (!file) {
      setPendingFile(null);
      setPendingRowCount(null);
      return;
    }
    setPendingFile(file);
    setPendingRowCount(await countXlsxDataRows(file));
  }

  function handleConfirmImport() {
    if (!pendingFile) return;
    importMutation.mutate(pendingFile);
  }

  const singular = entity.entityNounPlural.replace(/ies$/, "y").replace(/s$/, "");

  return (
    <div className="space-y-6">
      {/* Step 1: download template / export */}
      <div className="rounded-lg border bg-brand-muted p-4">
        <p className="mb-3 text-sm font-medium">1. Download a starting file</p>
        <div className="flex flex-wrap gap-2">
          {entity.templateUrl ? (
            <Button variant="outline" asChild data-testid="button-bulk-download-template">
              <a href={entity.templateUrl} download>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Download template
              </a>
            </Button>
          ) : null}
          <Button variant="outline" asChild data-testid="button-bulk-export">
            <a href={entity.exportUrl} download>
              <Download className="mr-2 h-4 w-4" />
              Export existing {entity.entityNounPlural}
            </a>
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The export includes your existing {entity.entityNounPlural} with property and room dropdowns pre-wired. Add new rows below the existing data, then re-upload. Each row is validated on import; duplicates are skipped, never overwritten.
        </p>
      </div>

      {/* Step 2: upload */}
      <div className="rounded-lg border p-4">
        <p className="mb-3 text-sm font-medium">2. Upload your filled-in file</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileSelect}
          disabled={importMutation.isPending}
          className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-foreground hover:file:bg-brand-accent"
          data-testid="input-bulk-file"
        />

        {pendingFile ? (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-md bg-brand-muted p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium" data-testid="text-bulk-filename">{pendingFile.name}</p>
              <p className="text-xs text-muted-foreground" data-testid="text-bulk-rowcount">
                {pendingRowCount === null
                  ? "Ready to import"
                  : `${pendingRowCount} ${entity.entityNounPlural} to import`}
              </p>
            </div>
            <Button
              onClick={handleConfirmImport}
              disabled={importMutation.isPending}
              data-testid="button-bulk-confirm-import"
            >
              {importMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing…</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" /> Import</>
              )}
            </Button>
          </div>
        ) : null}
      </div>

      {/* Step 3: results */}
      {result ? (
        <div className="space-y-3" data-testid="bulk-import-results">
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Import complete</AlertTitle>
            <AlertDescription>
              <span data-testid="text-bulk-created">{result.created} created</span>,{" "}
              <span data-testid="text-bulk-updated">{result.updated} updated</span>,{" "}
              <span data-testid="text-bulk-skipped">{result.skipped} skipped</span>,{" "}
              <span data-testid="text-bulk-errors">{result.errors} errors</span>.
            </AlertDescription>
          </Alert>

          {result.outcomes.some((o) => o.status !== "created") ? (
            <div className="max-h-60 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Row</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.outcomes
                    .filter((o) => o.status === "skipped" || o.status === "error")
                    .map((o) => (
                      <TableRow key={o.row} data-testid={`row-bulk-outcome-${o.row}`}>
                        <TableCell>{o.row}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-sm">
                            <AlertTriangle className="h-3.5 w-3.5 text-brand-accent" />
                            {o.status === "skipped" ? "Skipped" : "Error"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {o.name ? <span className="font-medium">{o.name}: </span> : null}
                          {o.message}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Generic, reusable "Bulk Import / Export" dialog shell. Drives the full flow
 * for one or more entities (tabbed when more than one). Feature pages wire it
 * up with their own endpoints + query key — property, appliance, and member
 * bulk import all reuse this shell without forking.
 */
export function BulkImportExportDialog({
  open,
  onOpenChange,
  title,
  description,
  tabs,
  templateUrl,
  exportUrl,
  importUrl,
  invalidateQueryKey,
  entityNounPlural,
}: BulkImportExportDialogProps) {
  // Track which panels are mid-import so dismissal can be blocked while any is.
  const [pendingTabs, setPendingTabs] = useState<Record<string, boolean>>({});
  const anyPending = Object.values(pendingTabs).some(Boolean);

  const entities: BulkEntityConfig[] =
    tabs && tabs.length > 0
      ? tabs
      : [
          {
            value: "default",
            label: entityNounPlural ?? "",
            templateUrl: templateUrl ?? "",
            exportUrl: exportUrl ?? "",
            importUrl: importUrl ?? "",
            invalidateQueryKey: invalidateQueryKey ?? [],
            entityNounPlural: entityNounPlural ?? "",
          },
        ];

  // Controlled active tab so we can block switching while a panel is
  // importing — Radix unmounts inactive <TabsContent>, which would otherwise
  // tear down the in-flight panel and drop its mutation result.
  const [activeTab, setActiveTab] = useState(entities[0].value);

  function makePendingHandler(value: string) {
    return (pending: boolean) =>
      setPendingTabs((prev) => (prev[value] === pending ? prev : { ...prev, [value]: pending }));
  }

  function handleOpenChange(next: boolean) {
    if (anyPending) return;
    if (!next) setPendingTabs({});
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl"
        onEscapeKeyDown={(e) => { if (anyPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (anyPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (anyPending) e.preventDefault(); }}
        data-testid="dialog-bulk-import-export"
      >
        <DialogHeader>
          <DialogTitle data-testid="text-bulk-title">{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {entities.length > 1 ? (
          <Tabs
            value={activeTab}
            onValueChange={(v) => { if (!anyPending) setActiveTab(v); }}
            className="w-full"
          >
            <TabsList>
              {entities.map((entity) => (
                <TabsTrigger
                  key={entity.value}
                  value={entity.value}
                  disabled={anyPending && entity.value !== activeTab}
                  data-testid={`tab-bulk-${entity.value}`}
                >
                  {entity.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {entities.map((entity) => (
              <TabsContent key={entity.value} value={entity.value} className="mt-4">
                <BulkEntityPanel entity={entity} onPendingChange={makePendingHandler(entity.value)} />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <BulkEntityPanel entity={entities[0]} onPendingChange={makePendingHandler(entities[0].value)} />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={anyPending}
            data-testid="button-bulk-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
